import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { IdCard, ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex flex-col">
      <nav className="brutal-border border-t-0 border-l-0 border-r-0 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-black flex items-center justify-center"><IdCard className="w-5 h-5 text-white" strokeWidth={2.5}/></div>
            <span className="font-display font-black text-xl tracking-tighter">DIGITAL CARD</span>
          </Link>
          <Link to="/login" className="font-mono text-xs uppercase tracking-wider font-bold hover:underline">Have account? Sign in</Link>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md bg-white brutal-border brutal-shadow-lg p-10">
          <div className="font-mono text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">// Free Account</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Start forging.</h1>
          <p className="text-sm text-gray-600 mb-8">Your first ID card is on us.</p>
          <button onClick={googleLogin} className="w-full flex items-center justify-center gap-3 bg-white text-black brutal-border brutal-shadow brutal-hover px-5 py-3 font-bold uppercase text-xs tracking-wider mb-6" data-testid="google-register-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-0.5 bg-black"></div>
            <span className="font-mono text-xs uppercase tracking-widest text-gray-500">OR</span>
            <div className="flex-1 h-0.5 bg-black"></div>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest font-bold mb-2">Full Name / Org</label>
              <input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} required className="brutal-input w-full" data-testid="reg-name"/>
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest font-bold mb-2">Email</label>
              <input type="email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} required className="brutal-input w-full" data-testid="reg-email"/>
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-widest font-bold mb-2">Password</label>
              <input type="password" minLength={6} value={form.password} onChange={e=>setForm({...form, password: e.target.value})} required className="brutal-input w-full" data-testid="reg-password"/>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-[#002FA7] text-white font-bold uppercase tracking-wider px-6 py-4 brutal-border brutal-shadow brutal-hover text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reg-submit">
              {loading ? "Creating..." : "Create Account"} <ArrowRight className="w-4 h-4"/>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
